/**
 * `nearestStationWith(service)` (spec §2.1, card C0): the nearest known station offering a service, by known-route
 * jumps, then (in this system) distance from the ship, then id for a stable order. Plus the `DockedAction` wiring:
 * the "where" text ("this system · 8.0 km", "2 jumps") and the reason line per service. Pure; runs at the
 * dashboard's refresh rate while it is open, never per frame.
 */
import type { DashboardConfig } from '../config'
import type { NearestStation } from '../parts'
import { formatDistance } from '../../../hud/reach/format'
import type { DockedActionModel, StationDirectory, StationHit, StationService } from './types'

export type DockedActionText = DashboardConfig['dockedAction']

function distance(a: readonly [number, number, number], b: readonly [number, number, number]): number {
  return Math.hypot(a[0] - b[0], a[1] - b[1], a[2] - b[2])
}

/** The nearest known station with `service`, or null when no known station offers it on a known route. */
export function nearestStationWith(dir: StationDirectory, service: StationService): StationHit | null {
  return nearest(dir, service)
}

/** The nearest known station of any kind (this system first, then fewest known-route jumps), or null. */
export function nearestKnownStation(dir: StationDirectory): StationHit | null {
  return nearest(dir, null)
}

function nearest(dir: StationDirectory, service: StationService | null): StationHit | null {
  let best: StationHit | null = null
  const jumpsBySystem = new Map<string, number | null>()
  for (const st of dir.stations) {
    if (service !== null && !st.services.includes(service)) continue
    let jumps = jumpsBySystem.get(st.systemId)
    if (jumps === undefined) {
      jumps = st.systemId === dir.hereSystemId ? 0 : dir.jumpsTo(st.systemId)
      jumpsBySystem.set(st.systemId, jumps)
    }
    if (jumps === null) continue
    const distanceM = jumps === 0 && dir.herePosition !== null ? distance(dir.herePosition, st.position) : null
    if (best === null || closer(jumps, distanceM, st.id, best)) best = { station: st, jumps, distanceM }
  }
  return best
}

function closer(jumps: number, distanceM: number | null, id: string, best: StationHit): boolean {
  if (jumps !== best.jumps) return jumps < best.jumps
  const d = distanceM ?? Infinity
  const bd = best.distanceM ?? Infinity
  if (d !== bd) return d < bd
  return id < best.station.id
}

function fill(template: string, values: Readonly<Record<string, string>>): string {
  return template.replace(/\{(\w+)\}/g, (m, k: string) => values[k] ?? m)
}

/** "this system · 8.0 km" (or "this system" with no ship position), "1 jump", "2 jumps". */
export function whereText(text: DockedActionText, hit: StationHit): string {
  if (hit.jumps === 0) return hit.distanceM === null ? text.thisSystem : fill(text.here, { distance: formatDistance(hit.distanceM) })
  return fill(hit.jumps === 1 ? text.jump : text.jumps, { n: String(hit.jumps) })
}

/** The `DockedAction` prop: name and where, or null. */
export function toNearestStation(text: DockedActionText, hit: StationHit | null): NearestStation | null {
  return hit === null ? null : { name: hit.station.name, where: whereText(text, hit) }
}

/** The reason line ("Dock at a yard to fit modules") and the nearest station for a docked-only action. */
export function dockedActionModel(text: DockedActionText, dir: StationDirectory, service: StationService): DockedActionModel {
  return { reason: text.reasons[service], hit: nearestStationWith(dir, service) }
}
